import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RouterProvider, createBrowserRouter, createHashRouter } from 'react-router-dom'
import { overlay } from 'overlay-kit'
import { StyleSheetManager } from 'styled-components'
import isPropValid from '@emotion/is-prop-valid'

import Notification from '@ahha/stableComponents/Notification/Notification'
import Dialog from '@ahha/stableComponents/Dialog/Dialog'
import { CampThemeProvider } from '@ahha/theme/CampTheme'
import { I18nProvider, useI18n } from '@ahha/i18n/i18n'
import { NotificationProvider } from '@/context/notificationContext'
import { QueryProvider } from '@/api/react-query/queryClient'

import i18nConfig from '@/locale/i18nConfig'
import { availableThemes } from '@/theme/ThemeConfig'
import { isElectron } from '@/utils/electron/electronUtils'
import { API } from '@/api'

import routes from '@/routes/routes'
import onPremiseRoutes from '@/routes/onPremiseRoutes'

import { ReducerType } from '@/stores/reducers'
import { setUser, UserType } from '@/stores/slices/userSlice'
import { RootState } from '@/stores'
import { setCurrentLanguage, setCurrentTheme } from '@/stores/slices/configSlice'

import ConfirmExitDialog from '@/components/ConfirmExitDialog/ConfirmExitDialog'

import classes from './App.module.scss'

const shouldForwardProp = (propName: string, target: unknown) => {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName)
  }
  return true
}

function App() {
  const dispatch = useDispatch()
  const user = useSelector<ReducerType, UserType>((state) => state.user)
  const currentConfig = useSelector((state: RootState) => state.config)

  const [showExit, setShowExit] = useState({ show: false, runningTasks: 0, waitingTasks: 0 })

  const { t } = useI18n()

  const router = useMemo(() => {
    if (isElectron()) {
      return (process.env.REACT_APP_TYPE === 'build' ? createHashRouter(onPremiseRoutes()) : createBrowserRouter(onPremiseRoutes()))
    }
    return createBrowserRouter(routes(user.isSignIn))
  }, [user.isSignIn])

  const willChangeLanguage = (lang: string) => {
    dispatch(setCurrentLanguage(lang))
  }

  const setThemeId = (theme: string) => {
    dispatch(setCurrentTheme(theme))
  }

  const preventTrackPadZoom = useCallback((e: WheelEvent) => {
    if (e.ctrlKey) {
      e.preventDefault()
      e.stopPropagation()
    }
  }, [])

  const refCallback = useCallback((node: HTMLDivElement) => {
    if (node) {
      node.addEventListener('wheel', preventTrackPadZoom, { passive: false })
    }
  }, [])

  const handleCancelExit = () => {
    setShowExit({ show: false, runningTasks: 0, waitingTasks: 0 })
    window.electronAPI?.confirmExit(false)
  }

  const handleConfirmExit = async () => {
    if (showExit.show) {
      overlay.open(({ isOpen, close }) => (
        <Dialog
          height="auto"
          open={isOpen}
          content={<p style={{ fontSize: '0.875rem', lineHeight: '1.375rem' }}>{t('app_exit_dialog_cancelAllInfo')}</p>}
          onClose={() => null}
        />
      ))
      setShowExit({ show: true, runningTasks: 0, waitingTasks: 0 })
      await API().OnPremise.cancelAllTasks()
    }
    window.electronAPI?.confirmExit(true)
  }

  const handleShowExitConfirmation = async () => {
    const { runningTasks, waitingTasks } = await API().Task.getAllTasksInProgress()
    if (!!runningTasks || !!waitingTasks) {
      setShowExit({ show: true, runningTasks, waitingTasks })
    } else {
      handleConfirmExit()
    }
  }

  useEffect(() => {
    if (isElectron()) {
      (async () => {
        const token = await window.electronAPI?.getStore('token')
        const workspaceId = await window.electronAPI?.getStore('workspaceId')
        const userId = await window.electronAPI?.getStore('userId')
        API().setToken(token)
        dispatch(setUser({
          token,
          user: {
            _id: userId,
            email: '',
            name: '',
            lastWorkspaceId: workspaceId,
            phoneNumber: '',
            profileColor: '',
            profileImageAccessibleUrl: '',
          },
          isSignIn: true,
        }))
      })()

      if (window.electronAPI) {
        window.electronAPI.onShowExitConfirmation(handleShowExitConfirmation)
      }
    }

    return () => {
      if (window.electronAPI) {
        window.electronAPI.removeAllListeners('show-exit-confirmation')
      }
    }
  }, [])

  return (
    <I18nProvider
      availableLanguages={i18nConfig}
      currentLanguage={currentConfig.currentLanguage}
      setCurrentLanguage={willChangeLanguage}
    >
      <NotificationProvider>
        <QueryProvider>
          <CampThemeProvider
            availableThemes={availableThemes}
            themeId={currentConfig.currentTheme}
            setThemeId={setThemeId}
          >
            <StyleSheetManager shouldForwardProp={shouldForwardProp}>
              <Notification />
              {/* TODO: styled component로 변경 */}
              {showExit.show && (
                <Dialog
                  open={showExit.show}
                  width="400px"
                  height="auto"
                  padding="0"
                  content={(
                    <ConfirmExitDialog
                      waitingTasks={showExit.waitingTasks}
                      runningTasks={showExit.runningTasks}
                      onCancel={handleCancelExit}
                      onExit={handleConfirmExit}
                    />
                  )}
                  onClose={handleCancelExit}
                />
              )}
              <div className={classes.App} ref={refCallback}>
                <div className={classes.layoutWrapper}>
                  <RouterProvider router={router} />
                </div>
                {/* {UIState.showProgressBlocker && <div className={classes.ProgressBlocker} />} */}
              </div>
            </StyleSheetManager>
          </CampThemeProvider>
        </QueryProvider>
      </NotificationProvider>
    </I18nProvider>
  )
}

export default App
